import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import {
  EvidenceDirection,
  EvidenceRole,
  NewsBarUnit,
} from "../../../enums/News";
import {
  CompanyNewsItem,
  NewsDayCount,
  NewsSearch,
  PublishedRange,
} from "../../../models/newsModels";
import { EVIDENCE_ROLE_RANK } from "./constants";

dayjs.extend(relativeTime);

export type NewsDayGroup = {
  date: string;
  items: CompanyNewsItem[];
};

export const strongestRole = (roles: EvidenceRole[]): EvidenceRole | null =>
  roles.reduce<EvidenceRole | null>(
    (best, role) =>
      best === null || EVIDENCE_ROLE_RANK[role] < EVIDENCE_ROLE_RANK[best]
        ? role
        : best,
    null,
  );

export const groupByDay = (items: CompanyNewsItem[]): NewsDayGroup[] =>
  items.reduce<NewsDayGroup[]>((groups, item) => {
    const date = dayjs(item.published_at).format("YYYY-MM-DD");
    const last = groups[groups.length - 1];
    if (last && last.date === date) {
      last.items.push(item);
      return groups;
    }
    return [...groups, { date, items: [item] }];
  }, []);

export const formatNewsDay = (date: string): string =>
  dayjs(date).format("D MMM");

const ISO_DAY = "YYYY-MM-DD";

export const trailingNewsRange = (days: number): PublishedRange => {
  const today = dayjs().startOf("day");
  return {
    published_from: today.subtract(days, "day").format(ISO_DAY),
    published_to: today.format(ISO_DAY),
  };
};

const DAYS_IN_YEAR = 365;

export const newsWindowLabel = (range: PublishedRange): string => {
  const end = dayjs(range.published_to);
  if (!end.isSame(dayjs(), "day")) {
    return formatNewsRange(range);
  }
  const days = end.diff(dayjs(range.published_from), "day");
  return days === DAYS_IN_YEAR ? "in 12 months" : `in ${days} days`;
};

export const searchPeriodLabel = (range: PublishedRange | null): string =>
  range ? `for ${formatNewsRange(range)}` : "in the latest news";

export const coversNewsRange = (
  search: NewsSearch,
  range: PublishedRange,
): boolean =>
  search.published_range !== null &&
  search.published_range.published_from <= range.published_from &&
  search.published_range.published_to >= range.published_to;

export const formatSearchedAt = (searchedAt: string): string =>
  dayjs(searchedAt).fromNow();

export const isSameNewsRange = (
  a: PublishedRange,
  b: PublishedRange,
): boolean =>
  a.published_from === b.published_from && a.published_to === b.published_to;

export const isTrailingNewsRange = (
  range: PublishedRange,
  days: number,
): boolean => isSameNewsRange(range, trailingNewsRange(days));

export const formatNewsSpan = (
  from: string,
  to: string,
): [string, string] => {
  const start = dayjs(from);
  const end = dayjs(to);
  const format =
    start.year() === end.year() && end.year() === dayjs().year()
      ? "D MMM"
      : "D MMM YY";
  return [start.format(format), end.format(format)];
};

export const formatNewsRange = (range: PublishedRange): string =>
  formatNewsSpan(range.published_from, range.published_to).join(" – ");

export const dayTotal = ({
  counts,
}: {
  counts: Record<EvidenceDirection, number>;
}): number => Object.values(counts).reduce((sum, count) => sum + count, 0);

export type NewsBarBucket = {
  start: string;
  end: string;
  unit: NewsBarUnit;
  counts: Record<EvidenceDirection, number>;
};

const DAYJS_UNIT: Record<NewsBarUnit, "day" | "week" | "month"> = {
  [NewsBarUnit.DAY]: "day",
  [NewsBarUnit.WEEK]: "week",
  [NewsBarUnit.MONTH]: "month",
};

const MIN_VISIBLE_DAYS = 14;
const MAX_DAILY_SPAN = 45;
const MAX_WEEKLY_SPAN = 180;

const unitForSpan = (days: number): NewsBarUnit =>
  days <= MAX_DAILY_SPAN
    ? NewsBarUnit.DAY
    : days <= MAX_WEEKLY_SPAN
      ? NewsBarUnit.WEEK
      : NewsBarUnit.MONTH;

const visibleDays = (days: NewsDayCount[]): NewsDayCount[] => {
  const firstWithNews = days.findIndex((day) => dayTotal(day) > 0);
  if (firstWithNews === -1) {
    return days.slice(-MIN_VISIBLE_DAYS);
  }
  return days.slice(
    Math.max(0, Math.min(firstWithNews, days.length - MIN_VISIBLE_DAYS)),
  );
};

export const bucketNewsDays = (days: NewsDayCount[]): NewsBarBucket[] => {
  const visible = visibleDays(days);
  const unit = unitForSpan(visible.length);
  const groups = visible.reduce<NewsDayCount[][]>((grouped, day) => {
    const last = grouped[grouped.length - 1];
    if (last && dayjs(last[0].day).isSame(dayjs(day.day), DAYJS_UNIT[unit])) {
      last.push(day);
      return grouped;
    }
    return [...grouped, [day]];
  }, []);
  return groups.map((group) => ({
    start: group[0].day,
    end: group[group.length - 1].day,
    unit,
    counts: totalsByDirection(group),
  }));
};

export const formatNewsBucket = (bucket: NewsBarBucket): string => {
  if (bucket.unit === NewsBarUnit.MONTH) {
    return dayjs(bucket.start).format("MMM YY");
  }
  if (bucket.start === bucket.end) {
    return formatNewsDay(bucket.start);
  }
  return formatNewsSpan(bucket.start, bucket.end).join(" – ");
};

export const totalsByDirection = (
  days: NewsDayCount[],
): Record<EvidenceDirection, number> =>
  days.reduce<Record<EvidenceDirection, number>>(
    (totals, day) => {
      Object.values(EvidenceDirection).forEach((direction) => {
        totals[direction] += day.counts[direction];
      });
      return totals;
    },
    {
      [EvidenceDirection.POSITIVE]: 0,
      [EvidenceDirection.NEGATIVE]: 0,
      [EvidenceDirection.MIXED]: 0,
      [EvidenceDirection.NEUTRAL]: 0,
      [EvidenceDirection.UNCLEAR]: 0,
    },
  );

export const isNewSince = (
  publishedAt: string,
  since: string | null,
): boolean => since !== null && dayjs(publishedAt).isAfter(dayjs(since));

export const isDayOnOrAfter = (date: string, since: string): boolean =>
  !dayjs(date).isBefore(dayjs(since).startOf("day"));

export const toSentenceLabel = (value: string): string => {
  const words = value.replace(/_/g, " ");
  return `${words.charAt(0).toUpperCase()}${words.slice(1)}`;
};
